import ActionButtons from '@components/Buttons/ActionButtons'
import { ContainedButtonVariant } from '@components/Buttons/ContainedButton'
import { TextButtonVariant } from '@components/Buttons/TextButton'
import { Typography, useTheme } from '@mui/material'
import { ProgramType } from '@swagger'
import { UTCYearString } from '@utils/dateUtility'
import formatYearsInText from '@utils/formatYearsInText'
import React from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmationModal from './ConfirmationModal'

type SendInvitationConfirmationModalProps = {
  onCancel: () => void
  onConfirm: () => void
  parentEmail: string
  programs: {
    programType: ProgramType
    semesterOneStartDate: Date
  }[]
  primaryButtonLoadingId?: string
}

const generateProgramInfoString = (
  programs: SendInvitationConfirmationModalProps['programs']
) => {
  const programsWithStartDates = programs.map((program) => {
    const year = UTCYearString(program.semesterOneStartDate)
    return `${program.programType} (${year})`
  })

  return programsWithStartDates.join(', ').concat('.')
}

export const SendInvitationConfirmationModal: React.FC<
  SendInvitationConfirmationModalProps
> = ({
  onCancel,
  onConfirm,
  parentEmail,
  programs,
  primaryButtonLoadingId,
}) => {
  const theme = useTheme()
  const { t } = useTranslation()

  const headerMessage = t(
    'Modals.SendInvitationConfirmationModal.Header',
    'You are about to send an invitation to {{parentEmail}}',
    { parentEmail }
  )

  const familyConfirmationMessage = t(
    'Modals.SendInvitationConfirmationModal.Body',
    'to join {{programsInformation}} Clicking "Send" will email the recipient an invitation with instructions on how to complete the next enrollment steps and pay the membership fee.',
    {
      programsInformation: generateProgramInfoString(programs),
    }
  )

  const familyConfirmationBody = (
    <Typography variant="body1" component="p" align="center">
      {formatYearsInText({
        text: familyConfirmationMessage,
        color: theme.palette.error.main,
      })}
    </Typography>
  )

  const dialogActions = (
    <ActionButtons
      primaryButtonLoadingId={primaryButtonLoadingId}
      primaryButtonLabel={ContainedButtonVariant.Send}
      secondaryButtonLabel={TextButtonVariant.Cancel}
      secondaryClick={onCancel}
      useBaseButton
    />
  )

  return (
    <ConfirmationModal
      isOpen
      dialogTitle={headerMessage}
      dialogContent={familyConfirmationBody}
      dialogActions={dialogActions}
      handleFormSubmit={onConfirm}
    />
  )
}
